/**
 * Threat visualization utilities for Cytoscape threat overlays.
 *
 * All functions operate imperatively on the cy instance — no React state,
 * no re-renders. Call from useOverlayData (App context), never from
 * InfrastructureGraph (which should remain overlay-unaware).
 */

import type cytoscape from 'cytoscape';
import type { ThreatOverlayPoint } from '@/store/overlayDataStore';

// ---------------------------------------------------------------------------
// Score → color (5-band, more granular than the 4-band threatColor in dns.ts)
// ---------------------------------------------------------------------------

export function threatGradientColor(score: number): string {
  if (score < 20) return '#56d364'; // green — clean
  if (score < 40) return '#7ee787'; // light green
  if (score < 60) return '#e3b341'; // yellow — suspicious
  if (score < 80) return '#f0883e'; // orange — high risk
  return '#ff4444';                  // red — malicious
}

// ---------------------------------------------------------------------------
// Pulse animation (DarkAPI layer)
// Border-width animation — confirmed working in Cytoscape canvas renderer
// ---------------------------------------------------------------------------

const animatingNodes = new Set<string>();

export function startThreatPulse(cy: cytoscape.Core, nodeId: string, color: string): void {
  if (animatingNodes.has(nodeId)) return;
  animatingNodes.add(nodeId);

  const dimColor = color + '66'; // ~40% opacity variant

  const pulse = (): void => {
    const node = cy.getElementById(nodeId);
    if (!node.length || !node.hasClass('flagged')) {
      animatingNodes.delete(nodeId);
      return;
    }
    node.animate(
      { style: { 'border-width': 5, 'border-color': color } as cytoscape.Css.Node },
      {
        duration: 700,
        easing: 'ease-in-out' as cytoscape.Css.TransitionTimingFunction,
        complete: () => {
          const n2 = cy.getElementById(nodeId);
          if (!n2.length || !n2.hasClass('flagged')) {
            animatingNodes.delete(nodeId);
            return;
          }
          n2.animate(
            { style: { 'border-width': 2.5, 'border-color': dimColor } as cytoscape.Css.Node },
            {
              duration: 700,
              easing: 'ease-in-out' as cytoscape.Css.TransitionTimingFunction,
              complete: pulse,
            },
          );
        },
      },
    );
  };

  pulse();
}

export function stopThreatPulse(cy: cytoscape.Core, nodeId: string): void {
  animatingNodes.delete(nodeId);
  const node = cy.getElementById(nodeId);
  if (!node.length) return;
  node.stop(true, false);
  // Restore default border via removeStyle so the stylesheet rule takes over
  node.removeStyle('border-width border-color');
}

// ---------------------------------------------------------------------------
// Score ring (ThreatDefense.ai layer) — overlay-* properties, static
// ---------------------------------------------------------------------------

export function applyScoreRing(
  cy: cytoscape.Core,
  nodeId: string,
  score: number,
  opacity: number,
): void {
  const node = cy.getElementById(nodeId);
  if (!node.length) return;
  const color = threatGradientColor(score);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node.style({
    'overlay-color': color,
    'overlay-opacity': Math.min(0.3, opacity * 0.35),
    'overlay-padding': '6px',
  } as any);
}

export function removeScoreRing(cy: cytoscape.Core, nodeId: string): void {
  const node = cy.getElementById(nodeId);
  if (!node.length) return;
  node.removeStyle('overlay-color overlay-opacity overlay-padding');
}

// ---------------------------------------------------------------------------
// High-level apply / remove (called from useOverlayData)
// ---------------------------------------------------------------------------

/**
 * Apply overlay visuals for a set of threat points.
 * DarkAPI → pulsing border. ThreatDefense → static overlay ring.
 * Sets node.data(overlayScore/Feeds/Source) for tooltip consumption.
 */
export function applyOverlayVisuals(
  cy: cytoscape.Core,
  adapterId: string,
  points: ThreatOverlayPoint[],
  config: { opacity: number; filterMinScore: number },
): void {
  // Performance guard: if adapter returns huge result set, skip animation for low-score nodes
  const largeBatch = points.length > 500;
  if (largeBatch) {
    console.warn(`[overlay] ${adapterId} returned ${points.length} points — skipping pulse for score < 30`);
  }

  for (const point of points) {
    if (point.score < config.filterMinScore) continue;
    if (largeBatch && point.score < 30) continue;

    // Match by label (domain or IP nodes only)
    const matches = cy.nodes().filter(
      (n) => n.data('label') === point.target && n.data('type') !== 'asn',
    );

    matches.forEach((node) => {
      // Per-adapter marker for multi-adapter removal tracking
      node.data(`_ov_${adapterId}`, true);

      // Highest-priority adapter wins the tooltip values (DarkAPI=1, TD=5)
      const existingSource = node.data('overlaySource') as string | undefined;
      const existingScore = (node.data('overlayScore') as number) ?? 0;
      const isDarkAPI = adapterId === 'darkapi';
      if (!existingSource || isDarkAPI || point.score > existingScore) {
        node.data('overlayScore', point.score);
        node.data('overlayFeeds', point.feeds);
        node.data('overlaySource', point.source);
      }

      node.addClass('flagged');

      if (adapterId === 'darkapi') {
        startThreatPulse(cy, node.id(), threatGradientColor(point.score));
      }
    });
  }
}

/**
 * Remove overlay visuals for a specific adapter.
 * Clears visual effects and node data markers.
 * Removes the `flagged` class only when no other adapter has flagged the node.
 */
export function removeOverlayVisuals(
  cy: cytoscape.Core,
  adapterId: string,
  points: ThreatOverlayPoint[],
): void {
  for (const point of points) {
    cy.nodes().filter(
      (n) => n.data('label') === point.target && n.data('type') !== 'asn',
    ).forEach((node) => {
      node.removeData(`_ov_${adapterId}`);

      if (adapterId === 'darkapi') {
        stopThreatPulse(cy, node.id());
      }

      // Remove flagged class and tooltip data only when no adapter has this node flagged
      const stillFlagged =
        (node.data('_ov_darkapi') as boolean | undefined);

      if (!stillFlagged) {
        node.removeClass('flagged');
        node.removeData('overlayScore');
        node.removeData('overlayFeeds');
        node.removeData('overlaySource');
      } else {
        // Remaining adapter may have different data — update tooltip values
        // Re-derive from DarkAPI if present (higher priority)
        const darkapiActive = node.data('_ov_darkapi') as boolean | undefined;
        if (!darkapiActive && adapterId === 'darkapi') {
          // DarkAPI was just removed; TD data becomes primary if present
          // overlaySource/Score/Feeds get updated by the next applyOverlayVisuals call
          // For now, clear stale DarkAPI values
          node.removeData('overlayScore');
          node.removeData('overlayFeeds');
          node.removeData('overlaySource');
        }
      }
    });
  }
}
